import React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import SettingsContext from 'src/features/administration/context/SettingsContext';
import { PERMISSION } from 'src/features/auth';
import { useListPricingPlanPeriodAdjustments } from 'src/features/pricingPlan/api/adjustments/useListPricingPlanPeriodAdjustments';
import { useGetPricingPlan } from 'src/features/pricingPlan/api/useGetPricingPlan';
import CreateAdjustmentFormModal from 'src/features/pricingPlan/components/form/adjustments/CreateAdjustmentFormModal';
import CellAdjustmentActions from 'src/features/pricingPlan/components/table/CellAdjustmentActions';
import { DAY_OF_WEEK_TRANSLATIONS_MAP, paymentTypeBadgeOptions } from 'src/features/pricingPlan/utils/constants';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import { PRICING_PLAN_STATUS, PricingPlanPeriodAdjustment } from 'src/features/pricingPlan/utils/types';
import useHasPermissions from 'src/hooks/useHasPermissions';
import { useParking } from 'src/providers/ParkingProvider';
import {
    CellBadge,
    CellDatetime,
    DataTable,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import usePagination from 'src/registry/hooks/usePagination';
import useSorting from 'src/registry/hooks/useSorting';
import { CURRENCY } from 'src/types';

const AdjustmentsTable = ({ currency }: { currency?: CURRENCY }) => {
    const { formatMessage, formatNumber } = useIntl();
    const { planId = '' } = useParams();
    const { parkingId } = useParking();
    const settingsData = React.useContext(SettingsContext);

    const managePricingPlanPeriodAdjustments = useHasPermissions([PERMISSION.MANAGE_PRICING_PLAN_PERIOD_ADJUSTMENTS]);

    // Default (standard) plans are read-only — hide the "new entry" button.
    const { data: pricingPlan } = useGetPricingPlan({
        planId,
        parkingId,
        config: { enabled: !!planId && !!parkingId },
    });
    const isSystem = !!pricingPlan?.is_system;

    const { querySortingOptions, ...sorting } = useSorting({
        initialSortingState: [{ id: 'valid_from', desc: true }],
    });
    const { queryPaginationOptions, ...pagination } = usePagination();

    const {
        data: pricingPlanPeriodAdjustments,
        isLoading,
        isError,
    } = useListPricingPlanPeriodAdjustments({
        planId,
        parkingId,
        ...querySortingOptions,
        ...queryPaginationOptions,
        config: {
            enabled: !!planId,
        },
    });

    const columnHelper = createColumnHelper<PricingPlanPeriodAdjustment>();

    const columns = [
        columnHelper.accessor('valid_from', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(pricingPlanMessages.activeFrom),
            },
        }),
        columnHelper.accessor('valid_to', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(pricingPlanMessages.activeTo),
            },
        }),
        columnHelper.accessor('payment_type', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: CellBadge,
            meta: {
                title: formatMessage(pricingPlanMessages.paymentType),
                badgeOptions: paymentTypeBadgeOptions,
            },
        }),
        columnHelper.accessor('day_of_week', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: ({ row }) =>
                row.original.day_of_week ? formatMessage(DAY_OF_WEEK_TRANSLATIONS_MAP[row.original.day_of_week]) : '-',
            meta: {
                title: formatMessage(pricingPlanMessages.day),
            },
        }),
        columnHelper.accessor('from_time', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: ({ row }) => {
                const fromTime = row.original.from_time;
                const toTime = row.original.to_time ? `- ${row.original.to_time}` : '';

                const displayHours = fromTime ? `${fromTime} ${toTime}` : (row.original.to_time ?? '-');

                return <div>{displayHours}</div>;
            },
            meta: {
                title: formatMessage(pricingPlanMessages.hours),
            },
            minSize: 170,
        }),
        columnHelper.accessor('adjustment_percent', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: ({ row }) =>
                row.original.adjustment_percent
                    ? `${(Number(row.original.adjustment_percent) * 100).toFixed(2)}%`
                    : '-',
            meta: {
                title: formatMessage(pricingPlanMessages.adjustmentPercent),
            },
        }),
        columnHelper.accessor('adjustment_amount', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableSorting: true,
            cell: ({ row }) => {
                const value = row.original.adjustment_amount;
                return value
                    ? formatNumber(value / 100, {
                          style: 'currency',
                          currency: currency ?? settingsData.default_currency,
                      })
                    : '-';
            },
            meta: {
                title: formatMessage(pricingPlanMessages.amount),
            },
        }),
        ...(managePricingPlanPeriodAdjustments && !isSystem
            ? [
                  columnHelper.display({
                      id: 'actions',
                      header: DataTableColumnHeader,
                      cell: (props) => (
                          <CellAdjustmentActions
                              {...props}
                              isPricingPlanPublished={pricingPlan?.status === PRICING_PLAN_STATUS.PUBLISHED}
                          />
                      ),
                      maxSize: 45,
                  }),
              ]
            : []),
    ];

    return (
        <DataTable<PricingPlanPeriodAdjustment, any>
            isLoading={isLoading}
            isError={isError}
            columns={columns}
            data={pricingPlanPeriodAdjustments}
            sorting={sorting}
            pagination={pagination}
            enableSearchParams={false}
            semantics={{
                title: formatMessage(pricingPlanMessages.adjustments),
                entryName: formatMessage(pricingPlanMessages.adjustment).toLowerCase(),
                entryNames: formatMessage(pricingPlanMessages.adjustments).toLowerCase(),
            }}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                ...(managePricingPlanPeriodAdjustments && !isSystem && { newEntry: CreateAdjustmentFormModal }),
            }}
        />
    );
};

export default AdjustmentsTable;
